import React from "react";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Gauge,
  Stethoscope,
  Calendar,
  Eye,
  ClipboardList,
  AlertTriangle,
  Phone,
  Users,
  BookOpen,
  Activity
} from "lucide-react";

interface NextStepsSidebarProps {
  currentSection?: string;
  onSectionClick: (sectionId: string) => void;
}

const navigationItems = [
  {
    id: "amiok-score",
    title: "AmIOK Score",
    icon: Gauge,
    description: "Risk assessment score"
  },
  {
    id: "triage-recommendations", 
    title: "Triage Recommendations",
    icon: Stethoscope,
    description: "Clinical triage guidance"
  },
  {
    id: "healthcare-contacts",
    title: "Healthcare Contacts",
    icon: Phone,
    description: "Emergency & primary contacts"
  },
  {
    id: "specialists",
    title: "Recommended Specialists",
    icon: Users,
    description: "Specialist referrals"
  },
  {
    id: "symptom-tracking",
    title: "Daily Symptom Tracking",
    icon: ClipboardList,
    description: "Self-assessment questions"
  },
  {
    id: "monitoring-areas",
    title: "Key Monitoring Areas",
    icon: Eye,
    description: "What to watch for"
  },
  {
    id: "resources",
    title: "Additional Resources",
    icon: BookOpen,
    description: "Educational materials"
  }
];

export function NextStepsSidebar({ currentSection, onSectionClick }: NextStepsSidebarProps) {
  const { state, setOpenMobile, openMobile } = useSidebar();
  const collapsed = state === "collapsed";

  const handleSectionClick = (sectionId: string) => {
    onSectionClick(sectionId);
    // Auto-collapse on mobile after selection
    if (openMobile) {
      setOpenMobile(false);
    }
  };

  const isActive = (sectionId: string) => currentSection === sectionId;

  return (
    <Sidebar 
      className={cn(
        "border-r border-sidebar-border bg-sidebar/95 backdrop-blur supports-[backdrop-filter]:bg-sidebar/80",
        "transition-all duration-300 ease-in-out shadow-sm",
        collapsed ? "w-14" : "w-64 md:w-80"
      )}
      collapsible="icon"
    >
      <SidebarContent className={collapsed ? "p-1 flex flex-col items-center" : "p-3 md:p-4"}>
        {/* Sidebar Toggle Button - Always visible */}
        <div className={cn("mb-3", collapsed ? "flex justify-center" : "flex justify-end")}>
          <SidebarTrigger className="hover:bg-sidebar-accent p-2 rounded-md border flex-shrink-0 bg-sidebar shadow-sm" />
        </div>
        
        <SidebarGroup className={collapsed ? "w-full flex flex-col items-center" : ""}>
          {!collapsed && (
            <SidebarGroupLabel className="text-xs md:text-sm font-semibold text-sidebar-foreground/70 mb-2 md:mb-4 flex items-center gap-2">
              <Activity className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden md:inline">Next Steps Dashboard</span>
              <span className="md:hidden text-xs">Dashboard</span>
            </SidebarGroupLabel>
          )}
          
          {collapsed && (
            <div className="flex justify-center mb-3 mt-2">
              <div className="p-1.5 rounded-md bg-sidebar-accent w-8 h-8 flex items-center justify-center">
                <Activity className="h-3.5 w-3.5 text-sidebar-accent-foreground" />
              </div>
            </div>
          )}
          
          <SidebarGroupContent className={collapsed ? "flex flex-col items-center w-full" : ""}>
            <SidebarMenu className={collapsed ? "space-y-2 flex flex-col items-center w-full" : "space-y-1"}>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.id} className={collapsed ? "flex justify-center" : ""}>
                    <SidebarMenuButton
                    onClick={() => handleSectionClick(item.id)}
                    className={cn(
                      "w-full transition-all duration-200 cursor-pointer relative group",
                      "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      "focus:bg-sidebar-accent focus:text-sidebar-accent-foreground",
                      isActive(item.id) && "bg-sidebar-primary text-sidebar-primary-foreground font-medium shadow-sm",
                      collapsed ? "p-2 justify-center w-8 h-8 flex items-center" : "p-2 md:p-3 justify-start"
                    )}
                  >
                    {collapsed ? (
                      <div className="relative flex items-center justify-center">
                        <item.icon className={cn(
                          "transition-colors",
                          isActive(item.id) ? "text-sidebar-primary-foreground" : "text-sidebar-foreground/60",
                          "h-3.5 w-3.5"
                        )} />
                        {/* Tooltip on hover */}
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-sidebar-popover text-sidebar-popover-foreground text-xs rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          {item.title}
                        </div>
                      </div>
                    ) : (
                        <div className="flex items-center gap-2 md:gap-3 w-full min-w-0">
                        <item.icon className={cn(
                          "flex-shrink-0 transition-colors",
                          isActive(item.id) ? "text-sidebar-primary-foreground" : "text-sidebar-foreground/60",
                          "h-3 w-3 md:h-4 md:w-4"
                        )} />
                        
                        <div className="flex flex-col items-start text-left flex-1 min-w-0">
                          <span className="text-xs md:text-sm font-medium leading-none truncate">
                            {item.title}
                          </span>
                          <span className="text-xs text-sidebar-foreground/60 mt-0.5 md:mt-1 truncate hidden md:block">
                            {item.description}
                          </span>
                        </div>
                      </div>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {!collapsed && (
          <div className="mt-6 p-3 bg-sidebar-accent/50 rounded-lg border border-sidebar-border">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-sidebar-accent-foreground" />
              <span className="text-sm font-medium text-sidebar-foreground">Navigation Tip</span>
            </div>
            <p className="text-xs text-sidebar-foreground/60 leading-relaxed">
              Click any section above to jump directly to that part of your health assessment.
            </p>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}